import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { buildEdgeFunctionHeaders } from "@/lib/session";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Link2, Link2Off, Loader2, CheckCircle2, XCircle, MessageCircle, Copy } from "lucide-react";

interface WhatsAppConnection {
  id: string;
  whatsapp_phone: string;
  display_name: string | null;
  enabled: boolean;
  created_at: string;
}

interface LinkInfo {
  code: string;
  bot_phone: string;
  expires_in_minutes: number;
}

const WhatsAppConnector = () => {
  const { user } = useAuth();
  const { toast } = useToast();

  const [connection, setConnection] = useState<WhatsAppConnection | null>(null);
  const [loadingConnection, setLoadingConnection] = useState(true);
  const [generatingCode, setGeneratingCode] = useState(false);
  const [linkInfo, setLinkInfo] = useState<LinkInfo | null>(null);
  const [disconnectOpen, setDisconnectOpen] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const fetchConnection = async () => {
    if (!user) return;
    const { data, error } = await supabase
      .from("whatsapp_connections")
      .select("*")
      .eq("user_id", user.id)
      .maybeSingle();

    if (error) {
      console.error("Failed to fetch WhatsApp connection:", error);
    } else {
      setConnection(data as WhatsAppConnection | null);
    }
    setLoadingConnection(false);
  };

  useEffect(() => {
    fetchConnection();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleConnect = async () => {
    setGeneratingCode(true);
    setLinkInfo(null);
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const headers = await buildEdgeFunctionHeaders();

      const res = await fetch(`${supabaseUrl}/functions/v1/whatsapp-link`, {
        method: "POST",
        headers,
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: "Failed to generate code" }));
        throw new Error(err.error || "Failed to generate code");
      }

      const data: LinkInfo = await res.json();
      setLinkInfo(data);
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    } finally {
      setGeneratingCode(false);
    }
  };

  const handleDisconnect = async () => {
    if (!user) return;
    setDisconnecting(true);
    const { error } = await supabase
      .from("whatsapp_connections")
      .delete()
      .eq("user_id", user.id);

    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      setConnection(null);
      setLinkInfo(null);
      toast({ title: "Disconnected", description: "Your WhatsApp account has been unlinked." });
    }
    setDisconnecting(false);
    setDisconnectOpen(false);
  };

  const copyCode = () => {
    if (!linkInfo) return;
    navigator.clipboard.writeText(linkInfo.code);
    toast({ title: "Copied", description: "Verification code copied to clipboard." });
  };

  if (loadingConnection) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col gap-6 p-6 overflow-y-auto max-w-2xl">
      <div>
        <h1 className="text-2xl font-semibold">WhatsApp Integration</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Link your WhatsApp number to chat with your data connectors directly from WhatsApp.
        </p>
      </div>

      {/* Connection status card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <MessageCircle className="h-5 w-5" />
              WhatsApp Account
            </CardTitle>
            {connection ? (
              <Badge variant="default" className="gap-1.5">
                <CheckCircle2 className="h-3 w-3" />
                Connected
              </Badge>
            ) : (
              <Badge variant="secondary" className="gap-1.5">
                <XCircle className="h-3 w-3" />
                Not connected
              </Badge>
            )}
          </div>
          <CardDescription>
            {connection
              ? "Your WhatsApp number is linked. Send a message to the bot to query your data sources."
              : "Connect your WhatsApp number to chat with your data connectors via the WhatsApp bot."}
          </CardDescription>
        </CardHeader>

        <CardContent className="flex flex-col gap-4">
          {connection ? (
            <>
              <div className="rounded-lg border border-border bg-muted/30 px-4 py-3 text-sm space-y-1">
                {connection.display_name && (
                  <p>
                    <span className="font-medium">Name:</span>{" "}
                    {connection.display_name}
                  </p>
                )}
                <p>
                  <span className="font-medium">Phone:</span>{" "}
                  +{connection.whatsapp_phone}
                </p>
                <p>
                  <span className="font-medium">Linked:</span>{" "}
                  {new Date(connection.created_at).toLocaleDateString()}
                </p>
              </div>

              <div className="flex gap-2">
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => setDisconnectOpen(true)}
                  className="gap-1.5"
                >
                  <Link2Off className="h-4 w-4" />
                  Disconnect
                </Button>
              </div>
            </>
          ) : (
            <>
              {linkInfo ? (
                <div className="flex flex-col gap-4">
                  <p className="text-sm text-muted-foreground">
                    Send the code below as a WhatsApp message to the bot number.
                    The code expires in <strong>{linkInfo.expires_in_minutes} minutes</strong>.
                  </p>

                  {/* Step 1: code */}
                  <div className="rounded-lg border border-border bg-muted/30 px-4 py-3 flex items-center justify-between">
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Your verification code</p>
                      <p className="text-3xl font-mono font-bold tracking-[0.3em]">
                        {linkInfo.code}
                      </p>
                    </div>
                    <Button variant="ghost" size="icon" onClick={copyCode} title="Copy code">
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>

                  {/* Step 2: bot number */}
                  <div className="rounded-lg border border-border bg-muted/30 px-4 py-3 text-sm">
                    <p className="text-xs text-muted-foreground mb-1">Send the code to</p>
                    <p className="font-medium">{linkInfo.bot_phone}</p>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleConnect}
                      disabled={generatingCode}
                    >
                      {generatingCode && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
                      Regenerate
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={fetchConnection}
                    >
                      Refresh status
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col gap-3">
                  <p className="text-sm text-muted-foreground">
                    Click <strong>Connect WhatsApp</strong> to generate a one-time verification
                    code. Send it to the bot on WhatsApp to complete the connection.
                  </p>
                  <Button
                    onClick={handleConnect}
                    disabled={generatingCode}
                    className="w-fit gap-1.5"
                  >
                    {generatingCode ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Link2 className="h-4 w-4" />
                    )}
                    Connect WhatsApp
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* How it works */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">How it works</CardTitle>
        </CardHeader>
        <CardContent>
          <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
            <li>Click <strong>Connect WhatsApp</strong> above to generate a 6-digit code.</li>
            <li>Open WhatsApp and send that code to the bot number shown.</li>
            <li>The bot confirms the link and your account is connected.</li>
            <li>
              Send any database question to the bot (e.g. <em>"Show me today's sales"</em>).
            </li>
            <li>The bot queries your configured data sources and replies with the results.</li>
          </ol>
        </CardContent>
      </Card>

      {/* Disconnect confirm */}
      <AlertDialog open={disconnectOpen} onOpenChange={setDisconnectOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disconnect WhatsApp?</AlertDialogTitle>
            <AlertDialogDescription>
              Your WhatsApp number will be unlinked. You won't be able to chat with your
              data sources from WhatsApp until you reconnect.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDisconnect} disabled={disconnecting}>
              {disconnecting && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
              Disconnect
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default WhatsAppConnector;
